import React, { useEffect, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { 
  BsEyeFill, 
  BsDownload, 
  BsPeople, 
  BsTrash, 
  BsCheckCircle, 
  BsXCircle 
} from 'react-icons/bs';
import { FiMail, FiPhone, FiUser, FiMessageSquare } from 'react-icons/fi';
import { BiDotsVerticalRounded } from 'react-icons/bi';

export default function ContactMessages() {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [searchMobile, setSearchMobile] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [actionDropdown, setActionDropdown] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    thisMonth: 0
  });

  const contactsPerPage = 10;

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = () => {
    setIsLoading(true);
    axios.get('http://localhost:5890/api/admin')
      .then(response => {
        if (response.data.success) {
          const contactsData = response.data.data;
          setContacts(contactsData);
          setFilteredContacts(contactsData);
          calculateStats(contactsData);
          setIsLoading(false);
        }
      })
      .catch(error => {
        console.error('Error fetching contacts:', error);
        setIsLoading(false);
      });
  };

  const calculateStats = (contactsData) => {
    const today = new Date().toDateString();
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    const todayCount = contactsData.filter(contact => {
      const contactDate = new Date(contact.created_at).toDateString();
      return contactDate === today;
    }).length;

    const monthCount = contactsData.filter(contact => {
      const contactDate = new Date(contact.created_at);
      return contactDate.getMonth() === currentMonth && 
             contactDate.getFullYear() === currentYear;
    }).length;

    setStats({
      total: contactsData.length,
      today: todayCount,
      thisMonth: monthCount
    });
  };

  useEffect(() => {
    let tempContacts = contacts;

    if (searchName.trim()) {
      tempContacts = tempContacts.filter(contact =>
        contact.name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (searchEmail.trim()) {
      tempContacts = tempContacts.filter(contact =>
        contact.email?.toLowerCase().includes(searchEmail.toLowerCase())
      );
    }

    if (searchMobile.trim()) {
      tempContacts = tempContacts.filter(contact =>
        contact.mobile?.includes(searchMobile)
      );
    }

    setFilteredContacts(tempContacts);
    setCurrentPage(1);
  }, [searchName, searchEmail, searchMobile, contacts]);

  const indexOfLastContact = currentPage * contactsPerPage;
  const indexOfFirstContact = indexOfLastContact - contactsPerPage;
  const currentContacts = filteredContacts.slice(indexOfFirstContact, indexOfLastContact);
  const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);

  const handleViewContact = (contact) => setSelectedContact(contact);
  const closeModal = () => setSelectedContact(null);

  const handleDeleteClick = (contact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
    setActionDropdown(null);
  };

  const confirmDelete = () => {
    if (contactToDelete) {
      axios.delete(`http://localhost:5890/api/users/${contactToDelete.id}`)
        .then(response => {
          if (response.data.success) {
            // Remove contact from state
            setContacts(contacts.filter(contact => contact.id !== contactToDelete.id));
            setFilteredContacts(filteredContacts.filter(contact => contact.id !== contactToDelete.id));
            setShowDeleteModal(false);
            setContactToDelete(null);
            fetchContacts(); // Refresh stats
          }
        })
        .catch(error => {
          console.error('Error deleting contact:', error);
        });
    }
  };

  const handleDownloadCSV = () => {
    const headers = ['ID', 'Name', 'Email', 'Mobile', 'Message', 'Date'];
    const rows = filteredContacts.map(contact => [
      contact.id,
      contact.name,
      contact.email,
      contact.mobile,
      contact.message.substring(0, 50) + (contact.message.length > 50 ? '...' : ''),
      new Date(contact.created_at).toLocaleDateString('en-US')
    ]);

    const csvContent = [
      headers.join(','),               
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `contact-messages-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="container py-4">
      {/* Dashboard Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-0" style={{ color: '#2c3e50' }}>Contact Messages</h2>
          <p className="text-muted mb-0">Manage and view contact form submissions</p>
        </div>
        <div className="d-flex align-items-center">
          <button 
            className="btn btn-primary d-flex align-items-center"
            onClick={handleDownloadCSV}
            disabled={filteredContacts.length === 0}
          >
            <BsDownload className="me-2" /> Export CSV
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px'
          }}>
            <div className="card-body text-white">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase mb-1">Total Messages</h6>
                  <h2 className="mb-0 fw-bold">{stats.total}</h2>
                </div>
                <div className="bg-white bg-opacity-10 p-3 rounded-circle">
                  <FiMessageSquare size={24} />
                </div>
              </div>
              <div className="mt-3">
                <span className="badge bg-white bg-opacity-10 text-white">
                  All contact submissions
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100" style={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            borderRadius: '12px'
          }}>
            <div className="card-body text-white">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase mb-1">Today</h6>
                  <h2 className="mb-0 fw-bold">{stats.today}</h2>
                </div>
                <div className="bg-white bg-opacity-10 p-3 rounded-circle">
                  <BsCheckCircle size={24} />
                </div>
              </div>
              <div className="mt-3">
                <span className="badge bg-white bg-opacity-10 text-white">
                  Messages received today
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100" style={{ 
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            borderRadius: '12px'
          }}>
            <div className="card-body text-white">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase mb-1">This Month</h6>
                  <h2 className="mb-0 fw-bold">{stats.thisMonth}</h2>
                </div>
                <div className="bg-white bg-opacity-10 p-3 rounded-circle">
                  <BsPeople size={24} />
                </div>
              </div>
              <div className="mt-3">
                <span className="badge bg-white bg-opacity-10 text-white">
                  Current month messages
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-person"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by name"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <FiMail />
                </span>
                <input
                  type="email"
                  className="form-control border-start-0"
                  placeholder="Search by email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <FiPhone />
                </span>
                <input
                  type="tel"
                  className="form-control border-start-0"
                  placeholder="Search by mobile"
                  value={searchMobile}
                  onChange={(e) => setSearchMobile(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-outline-secondary flex-grow-1"
                  onClick={() => {
                    setSearchName('');
                    setSearchEmail('');
                    setSearchMobile('');
                  }}
                >
                  Clear Filters
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={fetchContacts}
                >
                  <i className="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Data Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead style={{ 
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #e9ecef'
              }}>
                <tr>
                  <th className="text-center ps-4" style={{ width: '60px' }}>ID</th>
                  <th className="ps-3">Contact Person</th>
                  <th className="ps-3">Contact Info</th>
                  <th className="ps-3">Message Preview</th>
                  <th className="ps-3">Date</th>
                  <th className="text-center pe-4" style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : currentContacts.length > 0 ? (
                  currentContacts.map((contact) => (
                    <tr key={contact.id} className="hover-row">
                      <td className="text-center ps-4 fw-medium text-muted">
                        #{contact.id}
                      </td>
                      <td className="ps-3">
                        <div className="d-flex align-items-center">
                          <div 
                            className="avatar-circle me-3"
                            style={{ 
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              width: '40px',
                              height: '40px'
                            }}
                          >
                            {getInitials(contact.name)}
                          </div>
                          <div>
                            <div className="fw-medium">{contact.name}</div>
                            <small className="text-muted">Contact ID: {contact.id}</small>
                          </div>
                        </div>
                      </td>
                      <td className="ps-3">
                        <div className="d-flex flex-column">
                          <div className="text-truncate" style={{ maxWidth: '200px' }} title={contact.email}>
                            <FiMail className="me-2 text-primary" size={14} />
                            {contact.email}
                          </div>
                          <div className="text-muted small">
                            <FiPhone className="me-2 text-primary" size={14} />
                            {contact.mobile || 'Not provided'}
                          </div>
                        </div>
                      </td>
                      <td className="ps-3">
                        <div className="message-preview" style={{ maxWidth: '300px' }}>
                          {contact.message.length > 100 ? (
                            <>
                              {contact.message.substring(0, 100)}...
                              <button 
                                className="btn btn-link btn-sm p-0 ms-1"
                                onClick={() => handleViewContact(contact)}
                              >
                                Read more
                              </button>
                            </>
                          ) : (
                            contact.message
                          )}
                        </div>
                      </td>
                      <td className="ps-3">
                        <div className="small text-muted">
                          {formatDate(contact.created_at)}
                        </div>
                      </td>
                      <td className="text-center pe-4">
                        <div className="d-flex justify-content-center gap-2">
                          <button 
                            className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '32px', height: '32px' }}
                            onClick={() => handleViewContact(contact)}
                            title="View Message"
                          >
                            <BsEyeFill />
                          </button>
                          
                          <div className="dropdown">
                            <button 
                              className="btn btn-sm btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: '32px', height: '32px' }}
                              onClick={() => setActionDropdown(actionDropdown === contact.id ? null : contact.id)}
                              title="More Actions"
                            >
                              <BiDotsVerticalRounded />
                            </button>
                            
                            {actionDropdown === contact.id && (
                              <div className="dropdown-menu show shadow-sm" style={{ 
                                position: 'absolute', 
                                transform: 'translate3d(-120px, 32px, 0px)' 
                              }}>
                                <button 
                                  className="dropdown-item d-flex align-items-center text-danger"
                                  onClick={() => handleDeleteClick(contact)}
                                >
                                  <BsTrash className="me-2" />
                                  Delete Message
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="py-4">
                        <i className="bi bi-envelope-x display-4 text-muted mb-3"></i>
                        <h5 className="text-muted">No contact messages found</h5>
                        <p className="text-muted mb-0">No submissions match your search criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {filteredContacts.length > contactsPerPage && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="text-muted small">
            Showing {indexOfFirstContact + 1} to {Math.min(indexOfLastContact, filteredContacts.length)} of {filteredContacts.length} entries
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${currentPage === 1 && 'disabled'}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <li key={i} className={`page-item ${currentPage === pageNum && 'active'}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                );
              })}
              <li className={`page-item ${currentPage === totalPages && 'disabled'}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}

      {/* Message Details Modal */}
      {selectedContact && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0" style={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
              }}>
                <h5 className="modal-title">Contact Message Details</h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="row mb-4">
                  <div className="col-md-3 text-center">
                    <div 
                      className="avatar-circle mx-auto mb-3"
                      style={{ 
                        width: '80px', 
                        height: '80px',
                        fontSize: '2rem',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}
                    >
                      {getInitials(selectedContact.name)}
                    </div>
                    <h5 className="mb-1">{selectedContact.name}</h5>
                    <div className="text-muted">Contact ID: {selectedContact.id}</div>
                  </div>
                  <div className="col-md-9">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <h6 className="text-uppercase text-muted small mb-3">Contact Information</h6>
                            <div className="mb-3">
                              <div className="d-flex align-items-center mb-2">
                                <FiMail className="me-2 text-primary" />
                                <div>
                                  <div className="small text-muted">Email</div>
                                  <div className="fw-medium">{selectedContact.email}</div>
                                </div>
                              </div>
                              <div className="d-flex align-items-center">
                                <FiPhone className="me-2 text-primary" />
                                <div>
                                  <div className="small text-muted">Mobile</div>
                                  <div className="fw-medium">{selectedContact.mobile || 'Not provided'}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body">
                            <h6 className="text-uppercase text-muted small mb-3">Submission Details</h6>
                            <div className="mb-3">
                              <div className="small text-muted">Submitted On</div>
                              <div className="fw-medium">{formatDate(selectedContact.created_at)}</div>
                            </div>
                            <div>
                              <div className="small text-muted">Message ID</div>
                              <div className="fw-medium">#{selectedContact.id}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <h6 className="text-uppercase text-muted small mb-3">Message Content</h6>
                    <div className="message-content p-3 bg-light rounded">
                      {selectedContact.message}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeModal}
                >
                  Close
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={() => {
                    handleDeleteClick(selectedContact);
                    closeModal();
                  }}
                >
                  <BsTrash className="me-1" /> Delete Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0">
                <h5 className="modal-title text-danger">Confirm Delete</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <BsTrash className="text-danger mb-3" size={48} />
                  <h5>Delete this contact message?</h5>
                  <p className="text-muted">
                    Are you sure you want to delete the message from <strong>{contactToDelete?.name}</strong>? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={confirmDelete}
                >
                  Delete Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for the table enhancements */}
      <style>{`
        .hover-row:hover {
          background-color: #f8f9fa;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
        }
        
        .avatar-circle {
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          color: white;
        }
        
        .page-item.active .page-link {
          background-color: #667eea;
          border-color: #667eea;
        }
        
        .page-link {
          color: #667eea;
        }
        
        .dropdown-menu {
          z-index: 1050;
        }
        
        .message-preview {
          line-height: 1.5;
          color: #666;
        }
        
        .message-content {
          line-height: 1.6;
          white-space: pre-wrap;
          max-height: 300px;
          overflow-y: auto;
        }
      `}</style>
    </div>
  );
}